// src/services/notificationService.js - VERSION CORRIGÉE

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  addDoc,
  Timestamp,
  onSnapshot,
  or
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';

// Service central de notifications unifié pour clients et admins
// VERSION AVEC SÉPARATION MESSAGERIE/TIMELINE

/**
 * Types de notifications supportés
 */
export const NOTIFICATION_TYPES = {
  // MESSAGERIE (discussions WhatsApp-style)
  MESSAGE: 'message',
  MESSAGE_REPLY: 'message_reply',
  
  // TIMELINE (étapes du projet, notifications système)
  TIMELINE_STEP: 'timeline_step',
  TIMELINE_UPDATE: 'timeline_update',
  PROJECT_STATUS: 'project_status',
  PROJECT_CREATED: 'project_created',
  DOCUMENT_UPLOADED: 'document_uploaded',
  SYSTEM_ALERT: 'system_alert',
  
  // AUTRES (pour compatibilité)
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error'
};

/**
 * Détermine si une notification est de type "messagerie"
 */
export function isMessageNotification(notification) {
  return [
    NOTIFICATION_TYPES.MESSAGE,
    NOTIFICATION_TYPES.MESSAGE_REPLY
  ].includes(notification.type);
}

/**
 * Détermine si une notification est de type "timeline"
 */
export function isTimelineNotification(notification) {
  return [
    NOTIFICATION_TYPES.TIMELINE_STEP,
    NOTIFICATION_TYPES.TIMELINE_UPDATE,
    NOTIFICATION_TYPES.PROJECT_STATUS,
    NOTIFICATION_TYPES.PROJECT_CREATED,
    NOTIFICATION_TYPES.DOCUMENT_UPLOADED,
    NOTIFICATION_TYPES.SYSTEM_ALERT
  ].includes(notification.type);
}

/**
 * Vérifie si l'utilisateur actuel est un admin
 */
export async function isUserAdmin() {
  const currentUser = auth.currentUser;
  if (!currentUser) return false;
  
  try {
    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
    if (!userDoc.exists()) return false;
    
    const userData = userDoc.data();
    const role = userData.role?.toLowerCase();
    
    return ['admin', 'superadmin', 'super-admin', 'chef de projet', 'projectmanager', 'chargé d\'affaire'].includes(role);
  } catch (error) {
    console.error('Erreur lors de la vérification du rôle:', error);
    return false;
  }
}

/**
 * Récupère toutes les notifications (avec filtres)
 * CORRIGÉ : Filtrage par userId pour tous les utilisateurs
 */
export async function getNotifications(filters = {}) {
  const { readStatus, project, dateRange, deleted = false, dossierId, notificationType } = filters;
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('Utilisateur non authentifié');
  }

  let notificationsQuery;
  
  // Si un dossierId est fourni, on récupère toutes les notifications de ce dossier
  if (dossierId) {
    // 🔧 CORRECTION : Même pour un dossier, filtrer par userId
    notificationsQuery = query(
      collection(db, 'notifications'),
      where('dossierId', '==', dossierId),
      where('userId', '==', currentUser.uid)
    );
  } else {
    // 🔧 CORRECTION : TOUT LE MONDE (admin ou pas) voit seulement SES notifications
    notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', currentUser.uid)
    );
  }
  
  const snapshot = await getDocs(notificationsQuery);

  // Map et annotation
  let notifications = snapshot.docs.map(s => {
    const data = s.data();
    return { id: s.id, ...data, read: data.lu === true };
  });

  // Filtrage côté client pour le statut de suppression
  notifications = notifications.filter(n => {
    const isDeleted = n.deleted === true;
    return deleted ? isDeleted : !isDeleted;
  });

  // NOUVEAU : Filtrage par type de notification
  if (notificationType) {
    if (notificationType === 'messages') {
      notifications = notifications.filter(n => isMessageNotification(n));
    } else if (notificationType === 'timeline') {
      notifications = notifications.filter(n => isTimelineNotification(n));
    }
  }

  // Filtrage côté client pour les autres critères
  if (readStatus && readStatus !== 'all') {
    const shouldBeRead = readStatus === 'read';
    notifications = notifications.filter(n => n.read === shouldBeRead);
  }
  if (project && project !== 'all') {
    notifications = notifications.filter(n => n.projectId === project || n.dossierId === project);
  }
  if (dateRange?.[0] && dateRange?.[1]) {
    const [startDate, endRaw] = dateRange;
    const endDate = new Date(endRaw);
    endDate.setHours(23, 59, 59);
    notifications = notifications.filter(n => {
      const d = n.createdAt.toDate();
      return d >= startDate && d <= endDate;
    });
  }

  // Tri : plus récentes puis non lues en haut
  notifications.sort((a, b) => {
    if (a.read !== b.read) return a.read ? 1 : -1;
    return b.createdAt.toDate() - a.createdAt.toDate();
  });

  // 🔧 AJOUT - Enrichir les notifications avec les noms de dossiers
  const enrichedNotifications = await Promise.all(
    notifications.map(async (notification) => {
      if (notification.dossierId) {
        try {
          const dossierDoc = await getDoc(doc(db, 'dossiers', notification.dossierId));
          if (dossierDoc.exists()) {
            const dossierData = dossierDoc.data();
            notification.dossierTitle = dossierData.clientName || 
                                       dossierData.titre || 
                                       `Client ${notification.dossierId.slice(0,6)}`;
          }
        } catch (err) {
          console.error('Erreur enrichissement dossier:', err);
          notification.dossierTitle = `Dossier ${notification.dossierId.slice(0,6)}`;
        }
      }
      return notification;
    })
  );

  return enrichedNotifications;
}

/**
 * NOUVEAU : Récupère les discussions où l'utilisateur est participant
 */
export async function getDiscussions(filters = {}) {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('Utilisateur non authentifié');
  }

  try {
    // Charger les discussions où l'utilisateur est participant
    const discussionsQuery = query(
      collection(db, 'discussions'),
      where('participants', 'array-contains', currentUser.uid)
    );

    const snapshot = await getDocs(discussionsQuery);
    
    let discussions = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Calculer le nombre de messages non lus pour cet utilisateur
        unreadCount: (data.messages || []).filter(msg => 
          !msg.readBy?.includes(currentUser.uid) && msg.userId !== currentUser.uid
        ).length,
        // Enrichir avec les infos du dossier
        title: data.dossierTitre || `Discussion ${doc.id.slice(0, 8)}`,
        lastMessage: data.lastMessage || 'Aucun message'
      };
    });

    // Enrichir avec les infos des dossiers
    const enrichedDiscussions = await Promise.all(
      discussions.map(async (discussion) => {
        if (discussion.dossierId) {
          try {
            const dossierDoc = await getDoc(doc(db, 'dossiers', discussion.dossierId));
            if (dossierDoc.exists()) {
              const dossierData = dossierDoc.data();
              discussion.dossierTitle = dossierData.clientName || 
                                       dossierData.titre || 
                                       discussion.title;
            }
          } catch (err) {
            console.error('Erreur enrichissement dossier pour discussion:', err);
          }
        }
        return discussion;
      })
    );

    // Trier par dernière activité (plus récent en premier)
    enrichedDiscussions.sort((a, b) => {
      const dateA = a.updatedAt?.toDate?.() || a.createdAt?.toDate?.() || new Date(0);
      const dateB = b.updatedAt?.toDate?.() || b.createdAt?.toDate?.() || new Date(0);
      return dateB - dateA;
    });

    return enrichedDiscussions;
  } catch (error) {
    console.error('Erreur lors du chargement des discussions:', error);
    throw error;
  }
}

/**
 * NOUVEAU : Récupère uniquement les messages de discussion
 */
export async function getMessages(dossierId, filters = {}) {
  return await getNotifications({
    ...filters,
    dossierId,
    notificationType: 'messages'
  });
}

/**
 * NOUVEAU : Récupère uniquement les notifications timeline
 */
export async function getTimelineNotifications(filters = {}) {
  return await getNotifications({
    ...filters,
    notificationType: 'timeline'
  });
}

/**
 * Récupère les dossiers d'un utilisateur
 */
async function getUserDossiers(userId) {
  try {
    const dossiersQuery = query(
      collection(db, 'dossiers'),
      where('clientId', '==', userId)
    );
    const snapshot = await getDocs(dossiersQuery);
    return snapshot.docs.map(doc => doc.id);
  } catch (error) {
    console.error('Erreur lors de la récupération des dossiers:', error);
    return [];
  }
}

/**
 * NOUVEAU : Observe le compteur de messages non lus en temps réel
 * Spécifiquement pour la messagerie d'un dossier
 */
export function observeUnreadMessagesCount(dossierId, callback) {
  const user = auth.currentUser;
  if (!user) {
    callback(0, new Error('Utilisateur non authentifié'));
    return () => {};
  }
  
  if (!dossierId) {
    callback(0, new Error('Dossier ID requis'));
    return () => {};
  }
  
  const q = query(
    collection(db, 'notifications'),
    where('dossierId', '==', dossierId),
    where('userId', '==', user.uid),
    where('type', 'in', [NOTIFICATION_TYPES.MESSAGE, NOTIFICATION_TYPES.MESSAGE_REPLY])
  );
  
  return onSnapshot(q, snap => {
    try {
      const messages = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        read: doc.data().lu === true
      }));
      
      // Compter les messages non lus
      const unreadCount = messages.filter(m => !m.read).length;
      
      // Ajouter les replies non lues
      const unreadReplies = messages.reduce((count, message) => {
        const replies = message.replies || [];
        return count + replies.filter(r => !r.readBy?.includes(user.uid)).length;
      }, 0);
      
      callback(unreadCount + unreadReplies);
    } catch (error) {
      callback(0, error);
    }
  }, err => callback(0, err));
}

/**
 * NOUVEAU : Observe le compteur de notifications timeline non lues
 * Pour la cloche de notifications - CORRIGÉ sans await dans filter
 */
export function observeUnreadTimelineNotificationsCount(callback) {
  const user = auth.currentUser;
  if (!user) {
    callback(0, new Error('Utilisateur non authentifié'));
    return () => {};
  }
  
  const checkUnread = async () => {
    try {
      const notifications = await getTimelineNotifications();
      
      // Compter les non lues (déjà filtrées par userId dans getTimelineNotifications)
      const unreadCount = notifications.filter(n => !n.read && !n.deleted).length;
      
      // Les replies des notifications timeline comptent aussi
      const unreadReplies = notifications.reduce((count, notif) => {
        const replies = notif.replies || [];
        return count + replies.filter(r => !r.readBy?.includes(user.uid)).length;
      }, 0);
      
      callback(unreadCount + unreadReplies);
    } catch (error) {
      callback(0, error);
    }
  };
  
  // Check initial
  checkUnread();
  
  // Pour un vrai temps réel, on utilise un listener sur les notifications timeline
  const timelineTypes = [
    NOTIFICATION_TYPES.TIMELINE_STEP,
    NOTIFICATION_TYPES.TIMELINE_UPDATE,
    NOTIFICATION_TYPES.PROJECT_STATUS,
    NOTIFICATION_TYPES.PROJECT_CREATED,
    NOTIFICATION_TYPES.DOCUMENT_UPLOADED,
    NOTIFICATION_TYPES.SYSTEM_ALERT
  ];
  
  const q = query(
    collection(db, 'notifications'),
    where('userId', '==', user.uid),
    where('type', 'in', timelineTypes)
  );
  
  return onSnapshot(q, () => {
    checkUnread();
  }, err => callback(0, err));
}

/**
 * Observe le compteur de notifications non lues en temps réel (LEGACY)
 * Maintenu pour compatibilité - utilise maintenant la logique timeline
 */
export function observeUnreadNotificationsCount(callback) {
  return observeUnreadTimelineNotificationsCount(callback);
}

/**
 * Récupère les notifications page par page (avec support dossierId)
 */
export async function getNotificationsWithPagination(filters = {}, lastVisible = null) {
  const notifications = await getNotifications(filters);
  return { notifications, lastVisible: null, hasMore: false };
}

/**
 * Récupère la liste des projets/dossiers
 */
export async function getProjects() {
  const currentUser = auth.currentUser;
  if (!currentUser) return [];
  
  const isAdmin = await isUserAdmin();
  
  let dossiersQuery;
  if (isAdmin) {
    // Les admins voient tous les dossiers
    dossiersQuery = collection(db, 'dossiers');
  } else {
    // Les clients voient seulement leurs dossiers
    dossiersQuery = query(
      collection(db, 'dossiers'),
      where('clientId', '==', currentUser.uid)
    );
  }
  
  const snapshot = await getDocs(dossiersQuery);
  return snapshot.docs.map(d => ({
    id: d.id,
    name: d.data().titre || d.data().clientName || `Dossier ${d.id.slice(0,6)}`
  }));
}

/**
 * Observe une notification spécifique en temps réel
 */
export function observeNotification(notificationId, callback) {
  const ref = doc(db, 'notifications', notificationId);
  return onSnapshot(ref, snap => {
    if (!snap.exists()) {
      callback(null);
      return;
    }
    const data = snap.data();
    callback({ id: snap.id, ...data, read: data.lu === true });
  }, err => callback(null, err));
}

/**
 * Marque une notification comme lue
 */
export async function markNotificationAsRead(notificationId) {
  const user = auth.currentUser;
  if (!user) throw new Error('Utilisateur non authentifié');
  await updateDoc(doc(db, 'notifications', notificationId), { lu: true });
}

/**
 * Marque une réponse comme lue
 */
export async function markReplyAsRead(notificationId, replyId) {
  const user = auth.currentUser;
  if (!user) throw new Error('Utilisateur non authentifié');
  const ref = doc(db, 'notifications', notificationId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Notification non trouvée');
  const reps = snap.data().replies || [];
  const idx = reps.findIndex(r => r.id === replyId);
  if (idx < 0) throw new Error('Réponse non trouvée');
  if (!reps[idx].readBy) reps[idx].readBy = [];
  if (!reps[idx].readBy.includes(user.uid)) reps[idx].readBy.push(user.uid);
  await updateDoc(ref, { replies: reps });
}

/**
 * Ajoute une réponse à une notification
 */
export async function addReplyToNotification(notificationId, message) {
  const user = auth.currentUser;
  if (!user) throw new Error('Utilisateur non authentifié');
  
  // Récupérer les infos de l'utilisateur
  const userDoc = await getDoc(doc(db, 'users', user.uid));
  const userData = userDoc.exists() ? userDoc.data() : {};
  
  const ref = doc(db, 'notifications', notificationId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Notification non trouvée');
  
  const replyId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random()}`;
  const newReply = { 
    id: replyId, 
    message, 
    createdBy: user.uid,
    createdByEmail: user.email,
    createdByRole: userData.role || 'Utilisateur',
    createdAt: Timestamp.now(), 
    readBy: [user.uid] 
  };
  
  await updateDoc(ref, { 
    replies: arrayUnion(newReply),
    updatedAt: Timestamp.now()
  });
  return newReply;
}

/**
 * Met à jour une réponse existante
 */
export async function updateReply(notificationId, replyId, newMessage) {
  const user = auth.currentUser;
  if (!user) throw new Error('Utilisateur non authentifié');
  const ref = doc(db, 'notifications', notificationId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Notification non trouvée');
  const reps = snap.data().replies || [];
  const idx = reps.findIndex(r => r.id === replyId);
  if (idx < 0) throw new Error('Réponse non trouvée');
  reps[idx] = { ...reps[idx], message: newMessage, updatedAt: Timestamp.now() };
  await updateDoc(ref, { replies: reps });
  return reps[idx];
}

/**
 * Supprime une réponse
 */
export async function deleteReply(notificationId, replyId) {
  const ref = doc(db, 'notifications', notificationId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Notification non trouvée');
  const reps = snap.data().replies || [];
  const filtered = reps.filter(r => r.id !== replyId);
  await updateDoc(ref, { replies: filtered });
}

/**
 * Crée une nouvelle notification
 * MODIFIÉ pour supporter les nouveaux types
 */
export async function createNotification(userId, title, message, type = NOTIFICATION_TYPES.INFO, dossierId = null, options = {}) {
  const currentUser = auth.currentUser;
  
  const notification = {
    userId, // Peut être null pour les notifications de dossier
    dossierId, // IMPORTANT: toujours inclure le dossierId
    title,
    message,
    type, // Maintenant avec les nouveaux types NOTIFICATION_TYPES
    lu: false,
    deleted: false,
    createdAt: Timestamp.now(),
    createdBy: currentUser?.uid || userId,
    createdByEmail: currentUser?.email || options.createdByEmail,
    createdByRole: options.createdByRole || 'Système',
    replies: [],
    ...options // Permet d'ajouter des champs supplémentaires comme tag
  };

  const docRef = await addDoc(collection(db, 'notifications'), notification);
  return { id: docRef.id, ...notification };
}

/**
 * NOUVEAU : Crée une notification de message (messagerie)
 */
export async function createMessage(dossierId, title, message, options = {}) {
  return await createNotification(
    null, // userId null car c'est lié au dossier
    title,
    message,
    NOTIFICATION_TYPES.MESSAGE,
    dossierId,
    options
  );
}

/**
 * NOUVEAU : Crée une notification timeline
 */
export async function createTimelineNotification(userId, title, message, timelineType = NOTIFICATION_TYPES.TIMELINE_UPDATE, dossierId = null, options = {}) {
  return await createNotification(
    userId,
    title,
    message,
    timelineType,
    dossierId,
    options
  );
}

/**
 * Observe les notifications d'un dossier en temps réel
 */
export function observeDossierNotifications(dossierId, callback) {
  const user = auth.currentUser;
  if (!user) {
    callback([], new Error('Utilisateur non authentifié'));
    return () => {};
  }
  
  if (!dossierId) {
    callback([], new Error('Dossier ID requis'));
    return () => {};
  }
  
  const q = query(
    collection(db, 'notifications'),
    where('dossierId', '==', dossierId),
    where('userId', '==', user.uid)
  );
  
  return onSnapshot(q, snap => {
    const notifications = snap.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      read: doc.data().lu === true
    }));
    
    // Trier par date
    notifications.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
      return dateB - dateA;
    });
    
    callback(notifications);
  }, err => callback([], err));
}

/**
 * NOUVEAU : Observe uniquement les messages d'un dossier
 */
export function observeDossierMessages(dossierId, callback) {
  const user = auth.currentUser;
  if (!user) {
    callback([], new Error('Utilisateur non authentifié'));
    return () => {};
  }
  
  if (!dossierId) {
    callback([], new Error('Dossier ID requis'));
    return () => {};
  }
  
  const q = query(
    collection(db, 'notifications'),
    where('dossierId', '==', dossierId),
    where('userId', '==', user.uid),
    where('type', 'in', [NOTIFICATION_TYPES.MESSAGE, NOTIFICATION_TYPES.MESSAGE_REPLY])
  );
  
  return onSnapshot(q, snap => {
    const messages = snap.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      read: doc.data().lu === true
    }));
    
    // Trier par date (plus ancien en premier pour une discussion)
    messages.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
      return dateA - dateB; // Chronologique
    });
    
    callback(messages);
  }, err => callback([], err));
}

export default {
  // Fonctions principales
  getNotifications,
  getDiscussions,
  getMessages,
  getTimelineNotifications,
  getNotificationsWithPagination,
  getProjects,
  
  // Observateurs
  observeUnreadNotificationsCount,
  observeUnreadMessagesCount,
  observeUnreadTimelineNotificationsCount,
  observeNotification,
  observeDossierNotifications,
  observeDossierMessages,
  
  // Actions
  markNotificationAsRead,
  markReplyAsRead,
  addReplyToNotification,
  updateReply,
  deleteReply,
  
  // Création
  createNotification,
  createMessage,
  createTimelineNotification,
  
  // Utilitaires
  isUserAdmin,
  NOTIFICATION_TYPES,
  isMessageNotification,
  isTimelineNotification
};